import React, { useState, useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom/client';
import { ConfigProvider, theme as antdTheme } from 'antd';
import zhCN from 'antd/locale/zh_CN';
import enUS from 'antd/locale/en_US';
import zhTW from 'antd/locale/zh_TW';
import type { Locale } from 'antd/es/locale';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { setTokenGetter, setRefreshTokenGetter, setTokenSetter, setTokenExpiredHandler, setLanguageGetter } from '@tradermind/shared/api';
import { useAuthStore, useAppStore, setAuthStorage, setAppStorage } from '@tradermind/shared/stores';
import type { SupportedLanguage } from '@tradermind/shared/i18n';
import i18n from './i18n';
import App from './App';
import { webPushService } from './services/pushNotificationService';
import './index.css';

// Ant Design locale 映射
const antdLocales: Record<SupportedLanguage, Locale> = {
  'zh-CN': zhCN,
  'en-US': enUS,
  'zh-TW': zhTW,
};

const webStorage = {
  getItem: (name: string) => {
    const value = localStorage.getItem(name);
    return value ? JSON.parse(value) : null;
  },
  setItem: (name: string, value: unknown) => {
    localStorage.setItem(name, JSON.stringify(value));
  },
  removeItem: (name: string) => {
    localStorage.removeItem(name);
  },
};

setAuthStorage(webStorage);
setAppStorage(webStorage);

setTokenGetter(() => useAuthStore.getState().token);
setRefreshTokenGetter(() => useAuthStore.getState().refreshToken);
setTokenSetter((token: string) => useAuthStore.getState().setToken(token));
setTokenExpiredHandler(() => {
  useAuthStore.getState().logout();
  window.location.href = '/login';
});
setLanguageGetter(() => useAppStore.getState().language);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// 带语言和主题切换的 App 包装组件
function AppWrapper() {
  const language = useAppStore((state) => state.language);
  const themeMode = useAppStore((state) => state.theme);
  const [antdLocale, setAntdLocale] = useState<Locale>(zhCN);

  // 计算实际主题（处理 system 模式）
  const isDarkMode = useMemo(() => {
    if (themeMode === 'system') {
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    return themeMode === 'dark';
  }, [themeMode]);

  useEffect(() => {
    // 同步更新 i18n 语言
    i18n.changeLanguage(language);
    // 同步更新 Ant Design locale
    setAntdLocale(antdLocales[language] || zhCN);
  }, [language]);

  // 同步更新 HTML 根元素的主题类
  useEffect(() => {
    const root = document.documentElement;
    if (isDarkMode) {
      root.classList.add('dark');
      root.setAttribute('data-theme', 'dark');
    } else {
      root.classList.remove('dark');
      root.setAttribute('data-theme', 'light');
    }
  }, [isDarkMode]);

  // 监听系统主题变化
  useEffect(() => {
    if (themeMode !== 'system') return;
    
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => {
      const root = document.documentElement;
      if (mediaQuery.matches) {
        root.classList.add('dark');
        root.setAttribute('data-theme', 'dark');
      } else {
        root.classList.remove('dark');
        root.setAttribute('data-theme', 'light');
      }
    };
    
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [themeMode]);

  // 初始化 Web Push 通知
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  useEffect(() => {
    if (isAuthenticated && webPushService.isSupported()) {
      webPushService.initialize().then((success) => {
        if (success) {
          console.log('[WebPush] Service initialized');
        }
      });
    }
  }, [isAuthenticated]);

  return (
    <ConfigProvider
      locale={antdLocale}
      theme={{
        algorithm: isDarkMode ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
        token: {
          colorPrimary: '#3B82F6',
          colorSuccess: '#10B981',
          colorError: '#EF4444',
          colorWarning: '#F59E0B',
          borderRadius: 8,
        },
      }}
    >
      <App />
    </ConfigProvider>
  );
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <AppWrapper />
    </QueryClientProvider>
  </React.StrictMode>
);
